import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Inbox, Search, X } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { AssigneePicker } from '../components/AssigneePicker';
import { api, getAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Lead {
  id: string;
  tenant_id: string;
  /** Sprint 54 (Punkt 20) · joined fuer super_admin-Liste */
  tenant_slug?: string | null;
  tenant_name?: string | null;
  status: 'neu' | 'geprueft' | 'versendet' | 'abgeschlossen' | 'verworfen';
  name: string | null;
  firma: string | null;
  email: string | null;
  potenzial: number | null;
  messetag: string | null;
  created_at: string;
  erfasser_id: string;
  /** Sprint 78 · Erfasser-Name in der Uebersicht */
  erfasser_name?: string | null;
  /** Sprint 57 (Bug #3) · Inline-Picker in der Liste */
  uebernehmender_id?: string | null;
  uebernehmender_name?: string | null;
  visitenkarte_url: string | null;
}

interface Stats {
  by_status: Record<string, number>;
  total: number;
}

interface UserLite {
  id: string;
  full_name: string;
  email: string;
}

type StatusKey = 'neu' | 'geprueft' | 'versendet' | 'abgeschlossen' | 'verworfen';

interface FilterState {
  statuses: Set<StatusKey>;
  messetag: string | '';
  erfasserId: string | '';
  /** Sprint 78 · Filter nach Uebernehmender */
  uebernehmenderId: string | '';
  minPotenzial: number;
}

const STATUS_LABELS: Record<string, string> = {
  neu: 'NEU',
  // Sprint 73 · 'Geprüft' wird zu 'Versandbereit' (DB bleibt 'geprueft')
  geprueft: 'VERSANDBEREIT',
  versendet: 'VERSENDET',
  abgeschlossen: 'ABGESCHLOSSEN',
  verworfen: 'VERWORFEN',
};

const ALL_STATUSES: StatusKey[] = [
  'neu',
  'geprueft',
  'versendet',
  'abgeschlossen',
  'verworfen',
];

// Sprint 21 (S21.3) · UNITI 2026 Messetage
const MESSETAGE: Array<{ value: string; label: string }> = [
  { value: '2026-05-19', label: '19.05.2026' },
  { value: '2026-05-20', label: '20.05.2026' },
  { value: '2026-05-21', label: '21.05.2026' },
];

const EMPTY_FILTERS: FilterState = {
  statuses: new Set(),
  messetag: '',
  erfasserId: '',
  uebernehmenderId: '',
  minPotenzial: 1,
};

// Sprint 79 · Filter pro User in localStorage persistieren (ueberlebt
// Navigation, Tab-Schliessen und Browser-Restart). User-ID im Key damit
// zwei User am gleichen Browser/Geraet nicht die Filter des anderen sehen.
const FILTER_STORAGE_PREFIX = 'leadcraft:leads-filters:';
interface PersistedFilterShape {
  statuses: string[];
  messetag: string;
  erfasserId: string;
  uebernehmenderId: string;
  minPotenzial: number;
  search: string;
  statusFilter: string;
}

function loadPersistedFilters(userId: string | undefined): {
  filters: FilterState;
  search: string;
  statusFilter: string;
} | null {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${FILTER_STORAGE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedFilterShape;
    return {
      filters: {
        statuses: new Set((parsed.statuses ?? []) as StatusKey[]),
        messetag: parsed.messetag ?? '',
        erfasserId: parsed.erfasserId ?? '',
        uebernehmenderId: parsed.uebernehmenderId ?? '',
        minPotenzial: parsed.minPotenzial ?? 1,
      },
      search: parsed.search ?? '',
      statusFilter: parsed.statusFilter ?? '',
    };
  } catch {
    // Stale/kaputter Wert → ignorieren, mit Defaults weitermachen
    return null;
  }
}

function savePersistedFilters(
  userId: string | undefined,
  filters: FilterState,
  search: string,
  statusFilter: string,
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const payload: PersistedFilterShape = {
      statuses: Array.from(filters.statuses),
      messetag: filters.messetag,
      erfasserId: filters.erfasserId,
      uebernehmenderId: filters.uebernehmenderId,
      minPotenzial: filters.minPotenzial,
      search,
      statusFilter,
    };
    window.localStorage.setItem(
      `${FILTER_STORAGE_PREFIX}${userId}`,
      JSON.stringify(payload),
    );
  } catch {
    // Storage voll oder Privat-Mode → still failen
  }
}

function filterIsActive(f: FilterState): boolean {
  return (
    f.statuses.size > 0 ||
    !!f.messetag ||
    !!f.erfasserId ||
    !!f.uebernehmenderId ||
    f.minPotenzial > 1
  );
}

function activeFilterCount(f: FilterState): number {
  return (
    (f.statuses.size > 0 ? 1 : 0) +
    (f.messetag ? 1 : 0) +
    (f.erfasserId ? 1 : 0) +
    (f.uebernehmenderId ? 1 : 0) +
    (f.minPotenzial > 1 ? 1 : 0)
  );
}

interface FilterPill {
  key: string;
  label: string;
  remove: () => FilterState;
}

function buildFilterPills(
  f: FilterState,
  users: UserLite[],
): FilterPill[] {
  const out: FilterPill[] = [];
  if (f.statuses.size > 0) {
    out.push({
      key: 'status',
      label: `Status: ${Array.from(f.statuses)
        .map((s) => STATUS_LABELS[s] ?? s)
        .join(', ')}`,
      remove: () => ({ ...f, statuses: new Set() }),
    });
  }
  if (f.messetag) {
    const mt = MESSETAGE.find((m) => m.value === f.messetag);
    out.push({
      key: 'messetag',
      label: `Messetag: ${mt?.label ?? f.messetag}`,
      remove: () => ({ ...f, messetag: '' }),
    });
  }
  if (f.erfasserId) {
    const u = users.find((u) => u.id === f.erfasserId);
    out.push({
      key: 'erfasser',
      label: `Erfasser: ${u?.full_name ?? f.erfasserId.slice(0, 6)}`,
      remove: () => ({ ...f, erfasserId: '' }),
    });
  }
  if (f.uebernehmenderId) {
    const u = users.find((u) => u.id === f.uebernehmenderId);
    out.push({
      key: 'uebernehmender',
      label: `Übernimmt: ${u?.full_name ?? f.uebernehmenderId.slice(0, 6)}`,
      remove: () => ({ ...f, uebernehmenderId: '' }),
    });
  }
  if (f.minPotenzial > 1) {
    out.push({
      key: 'pot',
      label: `Potenzial ≥ ${f.minPotenzial}`,
      remove: () => ({ ...f, minPotenzial: 1 }),
    });
  }
  return out;
}

export function Leads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  // Sprint 79 · Filter aus localStorage restoren (user-scoped). Lazy-init,
  // damit der erste reload() bereits mit den richtigen Werten laeuft.
  const persisted = loadPersistedFilters(user?.id);
  const [statusFilter, setStatusFilter] = useState(persisted?.statusFilter ?? '');
  const [search, setSearch] = useState(persisted?.search ?? '');
  const [error, setError] = useState<string | null>(null);
  // Sprint 21 (S21.3) · Filter-Modal-State
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>(
    persisted?.filters ?? EMPTY_FILTERS,
  );
  const [users, setUsers] = useState<UserLite[]>([]);

  // Sprint 79 · Bei jeder Aenderung in localStorage spiegeln (user-scoped).
  useEffect(() => {
    savePersistedFilters(user?.id, filters, search, statusFilter);
  }, [user?.id, filters, search, statusFilter]);

  const reload = async (overrides?: {
    status?: string;
    q?: string;
    f?: FilterState;
  }) => {
    try {
      const params = new URLSearchParams();
      // Sprint 76 · Backend-Max ausnutzen (default war 100, Cap ist 500)
      params.set('page_size', '500');
      const f = overrides?.f ?? filters;
      // Status: Quick-Stat (legacy single) ODER Multiselect aus Filter-Modal
      if (overrides?.status) {
        params.set('status_filter', overrides.status);
      } else if (f.statuses.size > 0) {
        params.set('status_filter', Array.from(f.statuses).join(','));
      }
      if (overrides?.q ?? search) params.set('q', overrides?.q ?? search);
      if (f.messetag) params.set('messetag', f.messetag);
      if (f.erfasserId) params.set('erfasser_id', f.erfasserId);
      if (f.uebernehmenderId) params.set('uebernehmender_id', f.uebernehmenderId);
      if (f.minPotenzial > 1) params.set('min_potenzial', String(f.minPotenzial));
      const data = await api<Lead[]>(`/api/leads?${params}`);
      setLeads(data);
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Fehler');
    }
  };

  useEffect(() => {
    void reload();
    void api<Stats>('/api/leads/stats').then(setStats).catch(() => null);
    // Erfasser-Dropdown füttern (employee sieht nur sich selbst, das ist ok)
    void api<UserLite[]>('/api/users').then(setUsers).catch(() => null);
  }, []);

  function exportLeads(includeAttachments: boolean) {
    const token = getAccessToken();
    if (!token) return;
    const url = includeAttachments ? '/api/leads/export.zip' : '/api/leads/export.xlsx';
    const ext = includeAttachments ? 'zip' : 'xlsx';
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `leadcraft-leads-${new Date().toISOString().slice(0, 10)}.${ext}`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      });
  }

  if (!user) return null;

  return (
    <main className="app-shell leads-route">
      <AppHeader subtitle="LEADS" />
      <section className="app-shell__main">
        <div className="app-shell__card" style={{ maxWidth: 1100 }}>
          <div className="app-shell__card-label">
            LEADS · {stats ? `${stats.total} GESAMT` : '…'}
          </div>
          {/* Sprint 47 · 'Lead erfassen'-CTA über Heading auf Mobile · neben Heading auf Desktop */}
          <div className="users-toolbar">
            <h1 className="app-shell__card-title users-toolbar__title">Lead-Übersicht</h1>
            {user.role !== 'super_admin' && (
              <button
                type="button"
                className="users-toolbar__cta"
                onClick={() => navigate('/capture')}
              >
                + LEAD ERFASSEN
              </button>
            )}
          </div>

          {stats && (
            <div className="leads-stats">
              {Object.entries(stats.by_status).map(([status, count]) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => {
                    setStatusFilter(status);
                    void reload({ status, q: search });
                  }}
                  className={`leads-stat ${statusFilter === status ? 'leads-stat--active' : ''}`}
                >
                  <div className="leads-stat__count">{count}</div>
                  <div className="leads-stat__label">{STATUS_LABELS[status] ?? status}</div>
                </button>
              ))}
            </div>
          )}

          <div className="leads-filters">
            <input
              type="search"
              placeholder="Name, Firma, E-Mail…"
              className="auth-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void reload({ status: statusFilter, q: search });
              }}
            />
            <button
              type="button"
              className={`vm-btn ${filterIsActive(filters) ? 'vm-btn--active' : ''}`}
              onClick={() => setFilterModalOpen(true)}
              aria-label="Filter öffnen"
            >
              <Filter size={14} strokeWidth={2.5} aria-hidden style={{ marginRight: 4 }} />
              + FILTER
              {filterIsActive(filters) ? (
                <span className="leads-filter-badge">
                  {activeFilterCount(filters)}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className="vm-btn"
              onClick={() => {
                setStatusFilter('');
                setSearch('');
                setFilters(EMPTY_FILTERS);
                void reload({ f: EMPTY_FILTERS });
              }}
            >
              FILTER ZURÜCK
            </button>
            <button type="button" className="vm-btn" onClick={() => exportLeads(false)}>
              EXPORT XLSX
            </button>
            {/* Sprint 54 (Punkt 10) · ZIP-Variante mit Anhaengen + Visitenkarten */}
            <button
              type="button"
              className="vm-btn"
              onClick={() => exportLeads(true)}
              title="Excel + alle Anhaenge + Visitenkarten als ZIP"
            >
              EXPORT ZIP (mit Anhaengen)
            </button>
          </div>

          {/* Sprint 25 · Filter-Pills · einzeln entfernbar */}
          {filterIsActive(filters) ? (
            <div className="leads-filter-pills" aria-label="Aktive Filter">
              {buildFilterPills(filters, users).map((pill) => (
                <button
                  key={pill.key}
                  type="button"
                  className="leads-filter-pill"
                  onClick={() => {
                    const next = pill.remove();
                    setFilters(next);
                    void reload({ q: search, f: next });
                  }}
                  aria-label={`${pill.label} entfernen`}
                >
                  <span>{pill.label}</span>
                  <X size={12} strokeWidth={2.5} aria-hidden />
                </button>
              ))}
              <button
                type="button"
                className="leads-filter-pill leads-filter-pill--clear"
                onClick={() => {
                  setFilters(EMPTY_FILTERS);
                  setStatusFilter('');
                  void reload({ f: EMPTY_FILTERS });
                }}
              >
                ALLE ZURÜCKSETZEN
              </button>
            </div>
          ) : null}

          {error && <p className="auth-error">{error}</p>}
          {!leads && !error && <p className="app-shell__card-meta">LÄDT…</p>}

          {leads && leads.length === 0 && (
            <EmptyLeadsState
              hasFilter={Boolean(statusFilter || search)}
              onReset={() => {
                setStatusFilter('');
                setSearch('');
                void reload();
              }}
              canCapture={user.role !== 'super_admin'}
              navigate={navigate}
            />
          )}

          {filterModalOpen ? (
            <FilterModal
              initial={filters}
              users={users}
              onClose={() => setFilterModalOpen(false)}
              onApply={(next) => {
                setFilters(next);
                setStatusFilter('');
                setFilterModalOpen(false);
                void reload({ q: search, f: next });
              }}
            />
          ) : null}

          {leads && leads.length > 0 && (
            <>
              {/* Sprint 20 · S20.5 · Desktop ≥ 768px: Tabelle */}
              <div className="table-scroll-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      {/* Sprint 54 (Punkt 20) · Tenant-Spalte fuer super_admin */}
                      {user?.role === 'super_admin' ? <th>TENANT</th> : null}
                      <th>STATUS</th>
                      <th>NAME</th>
                      <th>FIRMA</th>
                      <th>E-MAIL</th>
                      <th>POT.</th>
                      <th>MESSETAG</th>
                      {/* Sprint 57 (Bug #3) · Übernimmt-Spalte mit Inline-Picker */}
                      <th>ÜBERNIMMT</th>
                      {/* Sprint 78 · Erfasser-Name */}
                      <th>ERFASSER</th>
                      <th>ERFASST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr
                        key={lead.id}
                        className="admin-table__row admin-table__row--clickable"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        {user?.role === 'super_admin' ? (
                          <td>
                            {lead.tenant_name ? (
                              <span className="tenant-tag">
                                {lead.tenant_slug?.toUpperCase() ?? '—'}
                              </span>
                            ) : (
                              <span className="admin-table__mono" style={{ opacity: 0.4 }}>
                                —
                              </span>
                            )}
                          </td>
                        ) : null}
                        <td className="admin-table__mono">
                          <span className={`status-pill status-pill--${lead.status}`}>
                            {STATUS_LABELS[lead.status] ?? lead.status}
                          </span>
                        </td>
                        <td>{lead.name ?? '—'}</td>
                        <td>{lead.firma ?? '—'}</td>
                        <td className="admin-table__mono">{lead.email ?? '—'}</td>
                        <td>{lead.potenzial ?? '—'}</td>
                        <td className="admin-table__mono">{lead.messetag ?? '—'}</td>
                        {/* Sprint 57 (Bug #3) · Übernimmt-Cell */}
                        <td onClick={(e) => e.stopPropagation()}>
                          <LeadAssigneeCell
                            lead={lead}
                            canEdit={
                              user?.role === 'operator_admin' ||
                              user?.role === 'dispatcher' ||
                              user?.role === 'super_admin'
                            }
                            onChanged={() => void reload()}
                          />
                        </td>
                        {/* Sprint 78 · Erfasser-Name */}
                        <td>{lead.erfasser_name ?? '—'}</td>
                        <td className="admin-table__mono">
                          {new Date(lead.created_at).toLocaleString('de-DE')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sprint 20 · S20.5 · Mobile < 768px: Card-View statt Tabelle */}
              <div className="leads-card-list" role="list">
                {leads.map((lead) => (
                  <button
                    key={lead.id}
                    type="button"
                    className="leads-card"
                    role="listitem"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    aria-label={`Lead ${lead.name ?? 'unbenannt'} öffnen`}
                  >
                    <div className="leads-card__row">
                      <span className="leads-card__name">
                        {lead.name ?? 'Unbenannt'}
                      </span>
                      <span className={`status-pill status-pill--${lead.status}`}>
                        {STATUS_LABELS[lead.status] ?? lead.status}
                      </span>
                    </div>
                    {lead.firma ? (
                      <div className="leads-card__firma">{lead.firma}</div>
                    ) : null}
                    <div className="leads-card__meta">
                      {lead.email ? `${lead.email} · ` : ''}
                      {new Date(lead.created_at).toLocaleDateString('de-DE')}
                      {lead.potenzial ? ` · POT ${lead.potenzial}` : ''}
                    </div>
                    {/* Sprint 78 · Erfasser/Übernimmt-Zeile in der Mobile-Card */}
                    {lead.erfasser_name || lead.uebernehmender_name ? (
                      <div className="leads-card__meta leads-card__meta--people">
                        {lead.erfasser_name ? `✎ ${lead.erfasser_name}` : ''}
                        {lead.erfasser_name && lead.uebernehmender_name ? ' · ' : ''}
                        {lead.uebernehmender_name ? `→ ${lead.uebernehmender_name}` : ''}
                      </div>
                    ) : null}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function EmptyLeadsState({
  hasFilter,
  onReset,
  canCapture,
  navigate,
}: {
  hasFilter: boolean;
  onReset: () => void;
  canCapture: boolean;
  navigate: (path: string) => void;
}) {
  if (hasFilter) {
    return (
      <div className="empty-state">
        <Search size={48} strokeWidth={1.5} className="empty-state__lucide" aria-hidden />
        <h2 className="empty-state__title">Keine Treffer für deinen Filter</h2>
        <p className="empty-state__text">
          Versuche andere Stichworte oder setze die Filter zurück.
        </p>
        <button type="button" className="auth-submit empty-state__cta" onClick={onReset}>
          FILTER ZURÜCKSETZEN
        </button>
      </div>
    );
  }
  return (
    <div className="empty-state">
      <Inbox size={48} strokeWidth={1.5} className="empty-state__lucide" aria-hidden />
      <h2 className="empty-state__title">Noch keine Leads erfasst</h2>
      <p className="empty-state__text">
        Sobald du auf der Messe Leads aufnimmst, erscheinen sie hier — sortiert,
        filterbar, exportierbar als Excel.
      </p>
      {canCapture ? (
        <button
          type="button"
          className="auth-submit empty-state__cta"
          onClick={() => navigate('/capture')}
        >
          ERSTEN LEAD ERFASSEN
        </button>
      ) : null}
    </div>
  );
}

/**
 * Sprint 21 (S21.3) · Filter-Modal mit Status-Multiselect, Messetag-Radio,
 * Erfasser-Dropdown, Min-Potenzial-Slider.
 *
 * Initial-State wird vom Parent gespiegelt; ANWENDEN dispatched onApply,
 * ABBRECHEN/X verwirft.
 */
function FilterModal({
  initial,
  users,
  onApply,
  onClose,
}: {
  initial: FilterState;
  users: UserLite[];
  onApply: (f: FilterState) => void;
  onClose: () => void;
}) {
  const [statuses, setStatuses] = useState<Set<StatusKey>>(new Set(initial.statuses));
  const [messetag, setMessetag] = useState(initial.messetag);
  const [erfasserId, setErfasserId] = useState(initial.erfasserId);
  const [uebernehmenderId, setUebernehmenderId] = useState(initial.uebernehmenderId);
  const [minPotenzial, setMinPotenzial] = useState(initial.minPotenzial);

  function toggleStatus(s: StatusKey) {
    const next = new Set(statuses);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setStatuses(next);
  }

  function reset() {
    setStatuses(new Set());
    setMessetag('');
    setErfasserId('');
    setUebernehmenderId('');
    setMinPotenzial(1);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card filter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="filter-modal__head">
          <h2 className="app-shell__card-title" style={{ margin: 0 }}>
            Leads filtern
          </h2>
          <button
            type="button"
            className="filter-modal__close"
            onClick={onClose}
            aria-label="Schließen"
          >
            <X size={20} strokeWidth={2.5} aria-hidden />
          </button>
        </div>

        <div className="filter-modal__section">
          <label className="auth-field__label">STATUS</label>
          <div className="bubble-group">
            {ALL_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className={`bubble ${statuses.has(s) ? 'bubble--active' : ''}`}
                onClick={() => toggleStatus(s)}
                aria-pressed={statuses.has(s)}
              >
                {STATUS_LABELS[s] ?? s}
              </button>
            ))}
          </div>
          <p className="form-field__hint">
            Mehrfachauswahl möglich. Leer = alle Status.
          </p>
        </div>

        <div className="filter-modal__section">
          <label className="auth-field__label">MESSETAG</label>
          <div className="bubble-group">
            <button
              type="button"
              className={`bubble ${!messetag ? 'bubble--active' : ''}`}
              onClick={() => setMessetag('')}
            >
              ALLE
            </button>
            {MESSETAGE.map((d) => (
              <button
                key={d.value}
                type="button"
                className={`bubble ${messetag === d.value ? 'bubble--active' : ''}`}
                onClick={() => setMessetag(d.value)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {users.length > 1 ? (
          <>
            <div className="filter-modal__section">
              <label className="auth-field__label" htmlFor="filter-erfasser">
                ERFASSER
              </label>
              <select
                id="filter-erfasser"
                className="auth-input"
                value={erfasserId}
                onChange={(e) => setErfasserId(e.target.value)}
              >
                <option value="">— alle —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-modal__section">
              <label className="auth-field__label" htmlFor="filter-uebernehmender">
                ÜBERNIMMT
              </label>
              <select
                id="filter-uebernehmender"
                className="auth-input"
                value={uebernehmenderId}
                onChange={(e) => setUebernehmenderId(e.target.value)}
              >
                <option value="">— alle —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : null}

        <div className="filter-modal__section">
          <label className="auth-field__label" htmlFor="filter-pot">
            POTENZIAL ≥ {minPotenzial}
          </label>
          <input
            id="filter-pot"
            type="range"
            min={1}
            max={10}
            step={1}
            value={minPotenzial}
            onChange={(e) => setMinPotenzial(parseInt(e.target.value, 10))}
            className="filter-modal__slider"
          />
          <div className="filter-modal__slider-marks">
            <span>1</span>
            <span>5</span>
            <span>10</span>
          </div>
        </div>

        <div className="filter-modal__actions">
          <button
            type="button"
            className="auth-submit"
            onClick={() =>
              onApply({ statuses, messetag, erfasserId, uebernehmenderId, minPotenzial })
            }
          >
            ANWENDEN
          </button>
          <button type="button" className="app-shell__logout" onClick={reset}>
            ZURÜCKSETZEN
          </button>
          <button type="button" className="app-shell__logout" onClick={onClose}>
            ABBRECHEN
          </button>
        </div>
      </div>
    </div>
  );
}

// Sprint 57 (Bug #3) · Inline-Picker fuer Uebernehmender in der Leads-Tabelle.
// canEdit: operator_admin + dispatcher + super_admin koennen aendern.
// e.stopPropagation auf der td verhindert dass Row-Click (navigate) feuert.
function LeadAssigneeCell({
  lead,
  canEdit,
  onChanged,
}: {
  lead: Lead;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [value, setValue] = useState(lead.uebernehmender_id ?? '');

  useEffect(() => {
    setValue(lead.uebernehmender_id ?? '');
  }, [lead.uebernehmender_id]);

  async function commit(newId: string) {
    setBusy(true);
    try {
      await api(`/api/leads/${lead.id}`, {
        method: 'PATCH',
        body: { uebernehmender_id: newId || null },
      });
      onChanged();
    } catch {
      setValue(lead.uebernehmender_id ?? '');
    } finally {
      setBusy(false);
      setEditing(false);
    }
  }

  if (!canEdit) {
    return (
      <span className="admin-table__mono">
        {lead.uebernehmender_name ?? '—'}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="lead-assignee-cell-btn"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        disabled={busy}
        title="Übernehmenden ändern"
      >
        {lead.uebernehmender_name ?? <span style={{ opacity: 0.5 }}>+ zuweisen</span>}
      </button>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <AssigneePicker
        value={value}
        onChange={(id) => {
          setValue(id);
          void commit(id);
        }}
        placeholder="Name suchen…"
      />
    </div>
  );
}
